// Native
import { useEffect, useState } from "react"

// Components
import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Avatar, AvatarGroup, Badge, Button, ButtonGroup, Divider, Flex, HStack, ModalBody, ModalFooter, Stack, StackDivider, Stepper, Td, Text, Tr, useDisclosure, useToast } from "@chakra-ui/react"
import { Dev, IconButton, Label, Modal, Profile, Show, Step, Table, Tag, Comment as CommentItem, Icon, Unverified, Form } from "components"

// Config
import { Moment, Mask, Enums } from "config"

// Hooks
import { useOrder } from "hooks"

// Screens
import Montage from "./montage"
import Quotation from "./quotation"
import Fiscal from "./fiscal"
import Collect from "./collect"
import Transit from "./transit"
import Withdraw from "./withdraw"
import Done from "./done"
import Init from "./init"
import { Comment } from "screens"
import { API } from "config"
// import {FaRegAddressBook} from "react-icons/fa";
import {FaRegCalendarPlus} from "react-icons/fa";

export default function Order({ data, ...props }){

    const toast = useToast()
    const order = useOrder(data?.id)
    const comment = useDisclosure()
    // const [ step, setStep ] = useState(0)
    const forShipping = order.data?.shipping_type === Enums.OrderShippingType.SHIPPING
    const [ loading, setLoading ] = useState(false)
    const [ admin_comments, setAdminComments ] = useState(order.data?.admin_comments)
    const [form, setForm] = useState(null)
    const tableColumns = [{ children: 'Qtde. Total', textAlign: 'center' }, { children: 'Produto' }]

    const hasPending =  (order?.data?.itens) ? Object.values(order.data.itens).some(item => item?.shipped_quantity !== null) : false;

    if (
        (order.data?.status === Enums.OrderStatus.APPROVED || hasPending) &&
        (order.data?.type !== Enums.OrderType.VOUCHER || order.data?.consultant_id ===  null) &&
        (order.data?.type !== Enums.OrderType.ADHESION)
    ) {
        tableColumns.push({ children: 'Qtde. Atendida', textAlign: 'center' }, { children:'Qtde. Pendente', textAlign: 'center' })
    }

    useEffect(() => {
        setForm(null)
    }, [data])

    const getStep = () => {
        if(forShipping) switch(order.data?.status){
            case Enums.OrderStatus.APPROVED: return 0; break;
            case Enums.OrderStatus.MONTAGE: return 0; break;
            case Enums.OrderStatus.QUOTATION: return 1; break;
            case Enums.OrderStatus.FISCAL: return 2; break;
            case Enums.OrderStatus.WAITING_SHIPPING: return 3; break;
            case Enums.OrderStatus.SHIPPING: return 5; break;
            case Enums.OrderStatus.DONE: return 6; break;
            default: return 0; break;
        }

        if(!forShipping) switch(order.data?.status){
            case Enums.OrderStatus.APPROVED: return 0; break;
            case Enums.OrderStatus.MONTAGE: return 0; break;
            case Enums.OrderStatus.FISCAL: return 1; break;
            case Enums.OrderStatus.WITHDRAWAL: return 2; break;
            case Enums.OrderStatus.DONE: return 3; break;
            default: return 0; break;
        }
    }

    const handleChange = (element, limit) => {
        const { name, value, id } = element.target;

        // 1. Permite vazio (para poder apagar)
        if (value === '') {
            setForm(prev => ({
                ...prev,
                [id]: {
                    ...prev?.[id],
                    [name]: '',
                    ...(name === 'shippedQuantity' ? { pendingQuantity: undefined } : {})
                }
            }));
            return;
        }

        // 2. Permite apenas dígitos
        if (!/^\d+$/.test(value)) return;

        // 3. Converte para número antes de validar limite
        const numericValue = Number(value);

        if (numericValue > limit) return;

        setForm(prev => ({
            ...prev,
            [id]: {
                ...prev?.[id],
                [name]: numericValue
            }
        }));

        if(name === 'shippedQuantity') {
            const itemQuantity = order?.data?.itens?.find(item => item.id === id)

            setForm(prev => ({
            ...prev,
            [id]: {
                ...prev?.[id],
                pendingQuantity: itemQuantity?.quantity - value
            }
        }));
        }
    }

    const handleChangeComments = async () => {
        setLoading('admin_comments')

        try{
            await API.post(`/orders/${order.data?.id}`, { admin_comments, _method: 'put' })
            toast({ title: 'Observações do pedido atualizadas!' })
            order.refetch()
        }catch(err){
            toast({ title: 'Não foi possível atualizar as observações do pedido. Tente novamente.', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    return(
        <Modal size="6xl" loading={order.isFetching} returnFocusOnClose={false} {...props}>
            <ModalBody>

                <Stepper colorScheme="orange" index={getStep()} mb={6}>
                    <Step title="Montagem" icon="package" />
                    { forShipping &&  <Step title="Cotação" icon="quotation" /> }
                    <Step title="Fiscal / Etiqueta" icon="fiscal" />
                    <Step title={ forShipping ? 'Coleta' : 'Retirada' } icon="transfer" />
                    { forShipping && <Step title="Em Trânsito" icon="transit" /> }
                    <Step title="Entregue" icon="check" />
                </Stepper>

                <Text fontSize="xl">Pedido <b>#{order.data?.cod}</b> do <Text as="span" textTransform="lowercase" children={order.data?.type_label}  /> <b>{order.data?.distributor_to?.name ?? order.data?.consultant?.customer.name}</b></Text>
                <HStack mt={1}>
                    <Badge children={Moment(order.data?.created_at).format('LLL')} />
                    <Badge colorScheme="orange" children={order?.data?.status_label} />
                </HStack>

                <Show visible={order.data?.is_legacy}>
                    <Divider my={4} />
                    <Unverified />
                </Show>

                <Accordion allowToggle my={4}>

                    <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">Detalhes do pedido <AccordionIcon /></AccordionButton>
                        <AccordionPanel>
                            <HStack spacing={8}>
                                <Label label="Cliente:" children={<Profile name={order.data?.distributor_to?.name ?? order.data?.consultant?.customer.name} />} />
                                <Label label="Data do pedido:" children={Moment(order.data?.created_at).format('LLL')} />
                                <Label label="Aprovado em:" children={Moment(order.data?.approved_at).format('LLL')} />
                                <Label label="Última atualização:" children={Moment(order.data?.updated_at).fromNow()} />
                            </HStack>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">Entrega <AccordionIcon /></AccordionButton>
                        <AccordionPanel>
                            <Stack spacing={4}>
                                <HStack spacing={8}>
                                    <Label label="Tipo de entrega:" children={order.data?.shipping_type_label} />
                                    <Show visible={order.data?.shipping_type === Enums.OrderShippingType.SHIPPING}>
                                        <Label label="Cidade:" children={`${order.data?.city?.name}, ${order.data?.city?.state_code}`} />
                                        <Label label="CEP:" children={order.data?.address_zip} />
                                        <Label label="Bairro/Setor:" children={order.data?.address_neighborhood} />
                                        <Label label="Endereço:" children={`${order.data?.address_line_1} ${order.data?.address_line_2 ?? ''} (${order.data?.address_number ?? ''}) | ${order.data?.address_complement ?? ''}`} />
                                    </Show>
                                </HStack>
                                <Show visible={order.data?.shipping_type === Enums.OrderShippingType.SHIPPING}>
                                    <HStack spacing={8}>
                                        <Label label="Transportadora:" children={order.data?.shipment?.label} />
                                        <Label label="Valor do frete:" children={`R$ ${Mask.price(order.data?.shipping_amount)}`} />
                                        <Label label="Código de rastreio" children={order.data?.shipping_track_code} />
                                    </HStack>
                                </Show>
                                <Show visible={order.data?.volumes?.length}>
                                    <Divider />
                                    <Text>Volumes adicionados:</Text>
                                    <Table size="sm" variant="striped" columns={[{ children: 'Rótulo' }, { children: 'Altura', isNumeric: true }, { children: 'Largura', isNumeric: true }, { children: 'Comprimento', isNumeric: true }, { children: 'Peso', isNumeric: true }]}>
                                        { order.data?.volumes?.map((item, index) => (
                                            <Tr key={item.id}>
                                                <Td><Badge>Volume 0{index+1}</Badge></Td>
                                                <Td isNumeric><Tag children={`${item.width} cm`} /></Td>
                                                <Td isNumeric><Tag children={`${item.height} cm`} /></Td>
                                                <Td isNumeric><Tag children={`${item.length} cm`} /></Td>
                                                <Td isNumeric><Tag children={`${item.weight} kg`} /></Td>
                                            </Tr>
                                        )) }
                                    </Table>
                                </Show>
                            </Stack>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">
                            Separar Itens do pedido <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                            <Table
                                variant="striped"
                                columns={tableColumns}
                            >
                                {order.data?.itens?.map(item => (
                                    <Tr key={item.id}>
                                        {/* Coluna 1: Quantidade */}
                                        <Td w="30px" textAlign={'center'} fontWeight={'bold'}>
                                            <Tag colorScheme="orange" variant="solid">{`${item.quantity}`}</Tag>
                                        </Td>
                                        {/* Coluna 2: Produto (Nome e Imagem) */}
                                        <Td>
                                            <Profile
                                                name={item?.product?.name ?? item.product_kit?.label}
                                                src={item?.product?.image_url ?? item.product_kit?.image_url}
                                            />
                                        </Td>
                                        {/* Coluna 3: Quantidade que será enviada */}
                                        {order.data?.status === Enums.OrderStatus.APPROVED &&
                                        (order.data?.type !== Enums.OrderType.VOUCHER || order.data?.consultant_id ===  null) &&
                                        (order.data?.type !== Enums.OrderType.ADHESION) &&
                                            <Td w="20px" fontSize={'sm'} textAlign={'center'}>
                                                <Form.Text
                                                    id={item?.id}
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]"
                                                    name="shippedQuantity"
                                                    fontSize={'sm'}
                                                    textAlign={'center'}
                                                    value={form?.[item?.id]?.shippedQuantity}
                                                    defaultValue={item?.quantity}
                                                    onChange={e => handleChange(e, item?.quantity)}
                                                />
                                            </Td>
                                        }
                                        {(order.data?.status !== Enums.OrderStatus.APPROVED && hasPending) &&
                                            <Td fontSize={'sm'} textAlign={'center'}>
                                                <Tag colorScheme="orange" variant="solid">{ (item?.shipped_quantity !== null) ? item.shipped_quantity : item?.quantity }</Tag>
                                            </Td>
                                        }
                                        {/* Coluna 4: Quantidade Pendente */}
                                        {(order.data?.status === Enums.OrderStatus.APPROVED || hasPending) &&
                                        (order.data?.type !== Enums.OrderType.VOUCHER || order.data?.consultant_id ===  null) &&
                                        (order.data?.type !== Enums.OrderType.ADHESION) &&
                                            <Td w="20px" fontSize={'sm'} textAlign={'center'}>
                                                <Tag colorScheme="orange" variant="solid">{ (!isNaN(form?.[item.id]?.shippedQuantity) && form?.[item.id]?.shippedQuantity < item.quantity) ? item.quantity - form?.[item.id]?.shippedQuantity : (item?.shipped_quantity !== null && item?.shipped_quantity !== undefined ? item?.quantity - item?.shipped_quantity : '--')}</Tag>
                                            </Td>
                                        }
                                    </Tr>
                                ))}
                            </Table>
                        </AccordionPanel>
                    </AccordionItem>

                    {/* <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">
                            Itens Pendentes <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                            <Table variant="striped">
                                <Td>
                                    <Tr>Sem pendências até o momento</Tr>
                                </Td>
                            </Table>
                        </AccordionPanel>
                    </AccordionItem> */}

                    <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">Histórico <AccordionIcon /></AccordionButton>
                        <AccordionPanel>
                            <Table variant="striped" columns={[{ isNumeric: true }, {}, {}]}>
                                { order.data?.history?.map(item => <Tr key={item.id}><Td isNumeric w="30px" children={Mask.dateTime(item.created_at)} /><Td>{item.label}</Td><Td>{ item.employee ? <Profile name={item.employee?.name} src={item?.employee?.avatar_url} /> : <Tag label="Ação automática do sistema" size="sm" children="Sistema" /> }</Td></Tr>) }
                            </Table>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">Comentários <AccordionIcon /></AccordionButton>
                        <AccordionPanel>
                            { order.data?.comments?.map(item => <CommentItem key={item.id} data={item} />) }
                            <Button w="100%" size="sm" children="Adicionar comentário" leftIcon={<Icon name="comment" />} onClick={comment.onOpen} mt={4} />
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                        <AccordionButton fontSize="sm" fontWeight="bold">Observações para o cliente <AccordionIcon /></AccordionButton>
                        <AccordionPanel>
                            <Form.Textarea defaultValue={order.data?.admin_comments} disabled={order.isFetching || !!loading} value={admin_comments} onChange={e => setAdminComments(e.target.value)} label="Insira as observações:" helper="Os comentários deste campo aparecerão para o cliente." />
                            <Button w="100%" mt={2} disabled={order.isFetching || !!loading} isLoading={loading==='admin_comments'} onClick={handleChangeComments}>Atualizar observações</Button>
                        </AccordionPanel>
                    </AccordionItem>

                </Accordion>

                {/* Iniciar */}
                { order.data?.status === Enums.OrderStatus.APPROVED && <Init data={order.data} step={getStep()} pendingQuantity={(form !== null) ? Object.fromEntries(Object.entries(form).map(([key, value]) => [key,{pendingQuantity: value?.pendingQuantity}])) : null}/> }

                {/* Montagem */}
                { order.data?.status === Enums.OrderStatus.MONTAGE && <Montage data={order.data} step={getStep()} /> }

                {/* Cotação */}
                { !!forShipping && order.data?.status === Enums.OrderStatus.QUOTATION && <Quotation data={order.data} step={getStep()} /> }

                {/* Fiscal */}
                { order.data?.status === Enums.OrderStatus.FISCAL && <Fiscal data={order.data} step={getStep()} /> }

                {/* Collect */}
                { order.data?.status === Enums.OrderStatus.WAITING_SHIPPING && <Collect data={order.data} step={getStep()} /> }

                {/* Withdraw */}
                { order.data?.status === Enums.OrderStatus.WITHDRAWAL && <Withdraw data={order.data} step={getStep()} /> }

                {/* Transit */}
                { order.data?.status === Enums.OrderStatus.SHIPPING && <Transit data={order.data} step={getStep()} /> }

                {/* Done */}
                { order.data?.status === Enums.OrderStatus.DONE && <Done data={order.data} step={getStep()} /> }

            </ModalBody>

            <ModalFooter borderTopWidth={1}>
                <Flex w="100%" justifyContent="space-between">
                    <HStack divider={<StackDivider />}>

                        <Text fontSize="xs" color="GrayText" children={`Última modificação ${Moment(order.data?.updated_at).fromNow()}`} />

                    </HStack>
                    <ButtonGroup size="sm" isDisabled={order.isFetching}>
                        <IconButton icon="print" label="Imprimir" onClick={() => window.open(order.data?.pdf_url)} />
                        <IconButton icon="fiscal" label="Etiqueta" onClick={() => window.open(order.data?.label_url)} />
                        <IconButton icon="refresh" label="Atualizar" isLoading={order.isFetching} onClick={order.refetch} />
                        <Dev data={order.data} />
                    </ButtonGroup>
                </Flex>
            </ModalFooter>

            <Comment.Publish {...comment} data={order.data} model="orders" onSuccess={order.refetch} />
        </Modal>
    )
}
